import {
	createFileRoute,
	useNavigate,
	useParams,
	useRouteContext,
} from "@tanstack/react-router";
import { graphql, useLazyLoadQuery, useMutation } from "react-relay";
import { toast } from "sonner";
import { Heading } from "@/components/heading";
import {
	LessonContentForm,
	type LessonContentFormSchemaValues,
} from "@/forms/lesson-content-form";
import type { cursosAulasEditConteudoQuery } from "./__generated__/cursosAulasEditConteudoQuery.graphql";
import type { cursosAulasEditConteudoUpdateMutation } from "./__generated__/cursosAulasEditConteudoUpdateMutation.graphql";

export const Route = createFileRoute(
	"/_logged/c/$club/(sidebar)/(admins)/area-de-membros/cursos/$slug/aulas/$lessonSlug/conteudo",
)({
	component: RouteComponent,
});

function RouteComponent() {
	const navigate = useNavigate({ from: Route.fullPath });
	const { slug: courseSlug, lessonSlug } = useParams({ from: Route.id });
	const { club } = useRouteContext({ from: Route.id });

	const data = useLazyLoadQuery<cursosAulasEditConteudoQuery>(
		graphql`
			query cursosAulasEditConteudoQuery($clubSlug: String!, $courseSlug: String!, $lessonSlug: String!) {
				club(slug: $clubSlug) {
					course(slug: $courseSlug) {
						id
						sections(status: ALL) {
							id
							name
						}
					}
				}
				courseClass(club: $clubSlug, course: $courseSlug, slug: $lessonSlug) {
					__typename
					id
					name
					slug
					description
					section {
						id
					}
					... on CourseContent {
						attachments {
							id
							name
							size
							signedUrl
						}
						video {
							__typename
							... on VideoFile {
								id
								url
								muxId
							}
							... on ExternalVideo {
								url
							}
						}
					}
				}
			}
		`,
		{ clubSlug: club.slug, courseSlug, lessonSlug },
	);

	const [updateCourseClass] =
		useMutation<cursosAulasEditConteudoUpdateMutation>(
			graphql`
			mutation cursosAulasEditConteudoUpdateMutation($payload: UpdateCourseClassInput!) {
				updateCourseClass(payload: $payload) {
					id
					name
					slug
					description
					section {
						id
					}
					... on CourseContent {
						attachments {
							id
							name
							size
							signedUrl
						}
						video {
							__typename
							... on VideoFile {
								id
								url
								muxId
							}
							... on ExternalVideo {
								url
							}
						}
					}
				}
			}
		`,
		);

	const lesson = data.courseClass;

	const sections =
		data.club.course.sections.map((section) => ({
			id: section.id,
			name: section.name,
		})) ?? [];

	// Build default values for content form
	const contentDefaultValues: LessonContentFormSchemaValues = {
		name: lesson.name,
		sectionId: lesson.section.id,
		videoFile:
			lesson.__typename === "CourseContent" &&
			lesson.video?.__typename === "VideoFile"
				? {
						id: lesson.video.id,
						url: lesson.video.url || "",
						muxId: lesson.video.muxId || undefined,
					}
				: null,
		externalVideoUrl:
			lesson.__typename === "CourseContent" &&
			lesson.video?.__typename === "ExternalVideo"
				? lesson.video.url
				: "",
		attachments:
			lesson.__typename === "CourseContent" && lesson.attachments
				? lesson.attachments.map((att) => ({
						id: att.id,
						url: att.signedUrl,
						name: att.name,
						size: att.size,
					}))
				: [],
		description: lesson.description
			? lesson.description
			: { type: "doc", content: [] },
		notifyStudents: false,
	};

	const handleContentSubmit = (values: LessonContentFormSchemaValues) => {
		return new Promise<void>((resolve, reject) => {
			updateCourseClass({
				variables: {
					payload: {
						id: lesson.id,
						name: values.name,
						section: values.sectionId,
						description: JSON.stringify(values.description),
						video: values.videoFile?.id || null,
						externalVideoUrl: values.externalVideoUrl || null,
						attachments: values.attachments.map((att) => ({ file: att.id })),
						notifyMembers: values.notifyStudents,
					},
				},
				onCompleted: (_response, errors) => {
					if (errors?.length) {
						toast.error(errors[0].message);
						reject(new Error(errors[0].message));
						return;
					}
					toast.success("Aula atualizada com sucesso!");
					navigate({
						to: "/c/$club/area-de-membros/cursos/$slug",
						params: { club: club.slug, slug: courseSlug },
					});
					resolve();
				},
				onError: (error) => {
					console.error("Error updating lesson:", error);
					toast.error("Erro ao atualizar aula. Tente novamente.");
					reject(error);
				},
			});
		});
	};

	return (
		<div className="size-full flex items-start justify-center py-4 md:py-8">
			<div className="p-6 w-full max-md:px-4 max-w-[700px] flex flex-col gap-6 border rounded-md">
				<Heading>Editar Aula de Conteúdo</Heading>

				<LessonContentForm
					sections={sections}
					defaultValues={contentDefaultValues}
					onSubmit={handleContentSubmit}
				/>
			</div>
		</div>
	);
}
